"use client";

import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { useTranslations } from "next-intl";

const LEFT = ["wedgeLeft1", "wedgeLeft2", "wedgeLeft3"] as const;
const RIGHT = ["wedgeRight1", "wedgeRight2", "wedgeRight3"] as const;

/**
 * Coding-assistant vs engine — the demo's opening punch, on the page.
 */
export function PitchWedge() {
  const t = useTranslations("Home");

  return (
    <section className="bg-slate-900/40 px-4 py-24 sm:px-6">
      <div className="mx-auto w-full max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mb-14 text-center"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-teal-light">
            {t("wedgeEyebrow")}
          </p>
          <h2 className="mx-auto max-w-[20.5rem] text-[clamp(1.5rem,6vw,3rem)] font-bold tracking-tight text-pretty text-slate-100 sm:max-w-none">
            {t("wedgeTitle")}
          </h2>
        </motion.div>

        <div className="relative grid gap-6 md:grid-cols-2">
          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl border border-slate-700/50 bg-slate-950/50 p-8 md:p-10"
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("wedgeLeftLabel")}
            </p>
            <h3 className="mb-8 text-2xl font-semibold text-slate-400">
              {t("wedgeLeftTitle")}
            </h3>
            <ul className="space-y-4">
              {LEFT.map((key) => (
                <li key={key} className="flex gap-3 text-slate-500">
                  <X
                    className="mt-0.5 h-5 w-5 shrink-0 text-slate-600"
                    aria-hidden
                  />
                  <span>{t(key)}</span>
                </li>
              ))}
            </ul>
          </motion.article>

          <div
            aria-hidden
            className="absolute left-1/2 top-1/2 z-10 hidden h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-slate-600 bg-slate-950 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 md:flex"
          >
            {t("wedgeVs")}
          </div>

          <motion.article
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-2xl border border-teal-primary/50 bg-gradient-to-br from-teal-primary/20 via-slate-900/80 to-slate-950 p-8 shadow-[0_0_70px_-18px_rgba(45,138,110,0.65)] md:p-10"
          >
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-teal-light">
              {t("wedgeRightLabel")}
            </p>
            <h3 className="mb-8 text-2xl font-semibold text-slate-100">
              {t("wedgeRightTitle")}
            </h3>
            <ul className="space-y-4">
              {RIGHT.map((key) => (
                <li key={key} className="flex gap-3 text-slate-200">
                  <Check
                    className="mt-0.5 h-5 w-5 shrink-0 text-teal-light"
                    aria-hidden
                  />
                  <span>{t(key)}</span>
                </li>
              ))}
            </ul>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
